<template>
  <form class="mx-auto w-full max-w-3xl" @submit="onSubmit">
    <BaseCard class="relative">
      <FormInputText
        name="name"
        label="Merchant Group Name"
      />

      <!-- Emails Section -->
      <label
        for="emails"
        class="mb-4 mt-6 block text-left text-sm font-medium text-background-fg/70"
        >Admin Emails</label
      >
      <div
        ref="emailsContainer"
        class="mb-6 flex max-h-[290px] flex-col overflow-y-scroll no-scrollbar"
      >
        <div
          v-if="adminFields.length === 0"
          class="text-center"
          >No admin emails</div
        >
        <div
          v-for="(field, index) in adminFields"
          :key="field.key"
          class="flex flex-col"
        >
          <Field
            #default="{ field: inputField, errorMessage }"
            :name="`admins[${index}].email`"
          >
            <div class="flex items-center space-x-4">
              <input
                ref="emailInputs"
                type="email"
                v-bind="inputField"
                class="form-input m-1 block w-full rounded-md border border-background-fg/8 text-sm font-semibold focus:outline-none sm:text-sm"
                :class="
                  errorMessage
                    ? 'border-error text-error placeholder-error focus:border-error focus:ring-error'
                    : 'focus:border-primary focus:ring-primary'
                "
                :placeholder="'Email ' + (index + 1)"
              />
              <BaseTableButton
                variant="danger"
                @click="removeEmail(index)"
              >
                Remove
              </BaseTableButton>
            </div>
            <p
              v-if="errorMessage"
              class="mt-1 pl-1 text-xs font-medium text-error"
            >
              {{ errorMessage }}
            </p>
          </Field>
        </div>

        <button
          type="button"
          class="mt-2 self-center whitespace-nowrap text-sm font-medium text-info/87 disabled:pointer-events-none disabled:text-background-fg/30"
          @click="addEmail"
        >
          Add Email
        </button>
      </div>

      <!-- Merchants Table Section -->
      <div class="my-5">
        <BaseTable
          :rows="formValues.merchants ?? []"
          :columns="merchantTableColumns"
          no-rows-text="No Merchants Selected"
        >
          <template #cell-actions="{ rowIndex }">
            <BaseTableButton
              variant="danger"
              @click="removeMerchant(rowIndex)"
            >
              Remove
            </BaseTableButton>
          </template>
        </BaseTable>
        <div class="mt-4 flex justify-end">
          <ActionButton @click="showSelectMerchantDialog"
            >Add Merchant</ActionButton
          >
        </div>
      </div>
      <FormSubmitButton
        class="self-center"
        :is-loading="isSubmitting"
      >
        Save Merchant Group
      </FormSubmitButton>
      <BaseInnerLoading v-if="isRetrievingMerchantGroup" />
    </BaseCard>
  </form>
  <SelectMerchantDialog
    :is-open="selectMerchantDialogIsOpen"
    :disable-merchant-predicate="newMerchantIsInvalid"
    @close="hideSelectMerchantDialog"
    @select-merchant="onSelectNewMerchant"
  />
</template>

<script setup lang="ts">
import type { TableColumn } from "./BaseTable.vue"
import { useFieldArray, Field } from "vee-validate"
import type { Merchant } from "@/services/merchants/listMerchants"

const props = defineProps<{
  merchantGroupId?: string // Only required if using to edit
}>()

const router = useRouter()

const emailsContainer = useTemplateRef("emailsContainer")
const emailInputs =
  useTemplateRef<Array<HTMLInputElement>>("emailInputs")

const {
  isOpen: selectMerchantDialogIsOpen,
  showDialog: showSelectMerchantDialog,
  hideDialog: hideSelectMerchantDialog,
} = useDialogState()

const {
  handleSubmit,
  values: formValues,
  resetForm,
  setErrors,
  errors,
  isSubmitting,
} = useMerchantGroupForm()

const {
  push: pushMerchant,
  remove: removeMerchant,
  fields: merchantFields,
} = useFieldArray<MerchantGroupFormMerchant>("merchants")

const {
  push: pushAdmin,
  remove: removeAdmin,
  fields: adminFields,
} = useFieldArray<MerchantGroupFormAdmin>("admins")

const onSubmit = handleSubmit(async (formValues) => {
  try {
    const params = {
      name: formValues.name,
      merchantIds: formValues.merchants.map(
        (merchant) => merchant.id
      ),
      adminEmails: formValues.admins.map(
        (admin) => admin.email
      ),
    }
    props.merchantGroupId
      ? await updateMerchantGroup(
          props.merchantGroupId,
          params
        )
      : await createMerchantGroup(params)

    props.merchantGroupId
      ? toast.success("Edit successful")
      : toast.success("Group created successfully")

    router.push({ name: "merchantGroupIndex" })
  } catch (error) {
    if (error instanceof ApiError) {
      const formErrors = error.requestErrors?.body ?? {}
      setErrors(formErrors)
      toast.error("Failed to save merchant group")
    } else {
      toast.error("Something went wrong")
    }
  }
})

const merchantTableColumns: Array<
  TableColumn<
    MerchantGroupFormMerchant,
    "merchantName" | "merchantMID" | "actions"
  >
> = [
  {
    id: "merchantName",
    header: "Merchant Name",
    getCellValue: (row) => row.name,
  },
  {
    id: "merchantMID",
    header: "MID",
    getCellValue: (row) => row.mid,
  },
  {
    id: "actions",
    autoWidth: true,
  },
]

const hasAdminEmailErrors = computed(() => {
  return Object.entries(errors.value).some(
    ([key]) =>
      key.startsWith("admins[") && key.endsWith(".email")
  )
})

const scrollToBottomEmailsDiv = (delay: number) => {
  // Scroll to the bottom when 4 or more emails
  setTimeout(() => {
    if (emailsContainer.value) {
      emailsContainer.value.scrollTo({
        top: emailsContainer.value.scrollHeight,
        behavior: "smooth",
      })
    }
  }, delay)
}

const addEmail = async () => {
  if (!hasAdminEmailErrors.value) {
    pushAdmin({ email: "" })
    scrollToBottomEmailsDiv(100)
    // Auto focus new input
    await nextTick()
    if (emailInputs.value === null) return
    const newEmailInput =
      emailInputs.value[emailInputs.value.length - 1]
    newEmailInput.focus()
  }
}

onMounted(() => {
  scrollToBottomEmailsDiv(300)
})

const removeEmail = (adminIndex: number) => {
  removeAdmin(adminIndex)
}

const onSelectNewMerchant = (newMerchant: Merchant) => {
  pushMerchant({
    name: newMerchant.merchantName,
    mid: newMerchant.merchantId,
    id: newMerchant.id,
    currency: newMerchant.currency,
  })
}

const newMerchantIsInvalid = computed(
  () => (newMerchant: Merchant) => {
    const merchantAlreadySelected =
      formValues.merchants?.some(
        (merchant) => merchant.id === newMerchant.id
      ) ?? false

    const merchantAlreadyGrouped =
      !!newMerchant.merchantGroup

    const merchantHasDifferentCurrency =
      merchantFields.value.some((merchant) => {
        return (
          merchant.value.currency !== newMerchant.currency
        )
      })
    const formIsCreate = props.merchantGroupId === undefined

    return (
      merchantAlreadySelected ||
      (formIsCreate && merchantAlreadyGrouped) ||
      (!formIsCreate &&
        merchantAlreadyGrouped &&
        newMerchant.merchantGroup?.id !==
          props.merchantGroupId) ||
      merchantHasDifferentCurrency
    )
  }
)

const isRetrievingMerchantGroup = ref(false)
onMounted(async () => {
  if (!props.merchantGroupId) return

  isRetrievingMerchantGroup.value = true

  try {
    const merchantGroup = await retrieveMerchantGroup(
      props.merchantGroupId
    )

    resetForm({ values: merchantGroup })
  } catch {
    toast.error(
      "Something went wrong fetching merchant group"
    )
  } finally {
    isRetrievingMerchantGroup.value = false
  }
})
</script>
